import {
	Box,
	Toolbar,
	IconButton,
	Avatar,
	Menu,
	MenuItem,
} from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';

import { useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { ReactComponent as CommentIcon } from 'common/assets/images/risk-gpt.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { MODULES } from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import {
	setRiskGptDrawer,
	setAppThemeDrawerState,
} from 'common/store/reducer/commonReducer';
import { isModuleExists, onLogout } from 'common/utils/authCheck';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import SideDrawer from '../../SideDrawer/SideDrawer';

import Theme from '../Theme/Theme';

import OpenRiskGPT from '../../../../features/OpenRiskGPT/OpenRiskGPT';
import { pwcIcon } from '../../../assets/images';
import { RootState } from '../../../store/AppStore';
import { ImgTagComponent, urlWithTenant } from '../../../utils/utilFunctions';

import styles from './SearchAppBar.module.scss';

const { riskGptColor } = styles;

const SearchAppBar = () => {
	const drawerWidth = 240;
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	let userInitial = 'JS';

	const handleClick: any = (event: any) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose: any = () => {
		setAnchorEl(null);
	};

	const riskGptDrawerState = useSelector(
		(state: RootState) => state.common.riskGptDrawer,
	);

	const riskGptDrawerExpand = useSelector(
		(state: RootState) => state.common.riskGptShouldExpand,
	);
	const riskGptAllowDrag = useSelector(
		(state: RootState) => state.common.riskGptAllowDrag,
	);
	const deleteAIGeneratedCardPayload = useSelector(
		(state: RootState) => state.common.deleteAIGeneratedCardPayload,
	);

	const isRegenerateMappingBtnClicked = useSelector(
		(state: RootState) => state.taxonomy.isRegenerateMappingBtnClicked,
	);

	/**
	 * Retrieves the appThemeDrawerState from the Redux store.
	 *
	 * @param {RootState} state - The root state of the application.
	 * @returns {AppThemeDrawerState} - The appThemeDrawerState from the Redux store.
	 */
	const appThemeDrawerState = useSelector(
		(state: RootState) => state.common.appThemeDrawerState,
	);

	/**
	 * Opens the theme popup and sets the app theme drawer state to true.
	 */
	const openThemePopup = useCallback(() => {
		handleClose();
		dispatch(setAppThemeDrawerState(true));
	}, [setAppThemeDrawerState]);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	if (loggedInUser) {
		userInitial = `${loggedInUser?.firstName?.split('')[0]}${
			loggedInUser?.lastName?.split('')[0]
		}`;
	}

	interface AppBarProps extends MuiAppBarProps {
		open?: boolean;
	}

	const AppBar = styled(MuiAppBar, {
		shouldForwardProp: (prop) => prop !== 'open',
	})<AppBarProps>(({ theme, open }) => ({
		zIndex: theme.zIndex.drawer + 1,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		...(open && {
			marginLeft: drawerWidth,
			width: `calc(100% - ${drawerWidth}px)`,
			transition: theme.transitions.create(['width', 'margin'], {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
		}),
	}));

	const riskGPTHandler = useCallback(() => {
		dispatch(setRiskGptDrawer(true));
	}, [setRiskGptDrawer]);

	const navigateToHome = () => {
		navigate(urlWithTenant('/home'));
	};

	const isRiskGptEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_RISK_GPT_ENABLED,
	);

	const isThemeEnabled = useFeatureFlag(FeatureFlagsKeysEnum.IS_THEME_ENABLED);

	const hasAskRiskLinkPermission =
		isRiskGptEnabled && isModuleExists(MODULES.ASK_RISK_LINK);
	return (
		<Box sx={{ flexGrow: 1 }}>
			<AppBar
				position="fixed"
				className={styles['app-header']}
				sx={{
					backgroundColor: 'white',
					color: 'black',
				}}
			>
				<Toolbar
					sx={{ display: 'flex', justifyContent: 'space-between' }}
					disableGutters
				>
					<IconButton
						color="inherit"
						aria-label="open drawer"
						edge="start"
						className={styles['app-logo']}
						disableRipple
						onClick={navigateToHome}
					>
						{ImgTagComponent(pwcIcon, 'pwc Risk Link')}
					</IconButton>
					<Menu
						disableAutoFocusItem
						id="basic-menu"
						anchorEl={anchorEl}
						open={Boolean(anchorEl)}
						onClose={handleClose}
						keepMounted
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						className={styles['profile-menu-style']}
						MenuListProps={{
							'aria-labelledby': 'basic-button',
						}}
					>
						<MenuItem
							onClick={handleClose}
							disabled
							className={styles['profile-item']}
						>
							Profile
						</MenuItem>
						{isThemeEnabled && (
							<MenuItem
								onClick={openThemePopup}
								className={styles['profile-item']}
								data-testid="theme-menu"
							>
								Themes
							</MenuItem>
						)}
						<MenuItem
							data-testid="logout-btn"
							onClick={() =>
								onLogout(
									deleteAIGeneratedCardPayload,
									isRegenerateMappingBtnClicked,
								)
							}
							className={styles['profile-item']}
						>
							Logout
						</MenuItem>
					</Menu>

					<div className={styles['icons-alignment']}>
						{hasAskRiskLinkPermission && (
							<ButtonComp
								compClass={styles['risk-gpt-button']}
								btnType="secondary"
								clickHandler={riskGPTHandler}
								startIcon={
									<Box className={styles['icons-alignment']}>
										<CommentIcon fill={riskGptColor} />
									</Box>
								}
								data-testid="btn-askRiskGPT"
							>
								Ask Risk Link
							</ButtonComp>
						)}
						<IconButton
							size="large"
							aria-label="account of current user"
							aria-controls="menu-appbar"
							aria-haspopup="true"
							onClick={handleClick}
							data-testid="menu-item"
							color="inherit"
						>
							<Avatar className={styles.avatarprofile}>{userInitial}</Avatar>
						</IconButton>
					</div>
				</Toolbar>
			</AppBar>
			<SideDrawer
				className={styles['riskgpt-drawer']}
				drawerState={riskGptDrawerState}
				shouldExpand={riskGptDrawerExpand}
				pageModule={MODULES.ASK_RISK_LINK}
				allowDrag={riskGptAllowDrag}
			>
				<OpenRiskGPT />
			</SideDrawer>
			<SideDrawer
				className={styles['theme-drawer']}
				drawerState={appThemeDrawerState}
			>
				<Theme />
			</SideDrawer>
		</Box>
	);
};

export default SearchAppBar;
